/**
 * Fase 2B · B1 · TitularFirstRun
 * Wizard 4 pasos post-registro para el TITULAR.
 * Backend: escribe server-side vía PUT /api/org/me y POST /api/services.
 * URL query ?step=center|services|availability|value permite deep-link (retomar).
 * Reutiliza OnboardingWizard.jsx (Fase 2A).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";
import OnboardingWizard from "../../components/onboarding/OnboardingWizard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Storefront, Wrench, Clock, Sparkle, CheckCircle, ArrowRight, Plus, X as XIcon,
  MapPin, Phone as PhoneIcon, Buildings, CurrencyEur,
} from "@phosphor-icons/react";

const DAYS_ES = ["L", "M", "X", "J", "V", "S", "D"];
const DAY_NAMES = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

const STEP_IDS = ["center", "services", "availability", "value"];

export default function TitularFirstRun() {
  const { user, org, refresh } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [initialData, setInitialData] = useState(null);
  const [activationStatus, setActivationStatus] = useState(null);

  const preferredStep = searchParams.get("step");
  const preferredIdx = preferredStep ? STEP_IDS.indexOf(preferredStep) : -1;

  // Load current org + activation
  useEffect(() => {
    (async () => {
      try {
        const [orgResp, statusResp] = await Promise.all([
          api.get("/org/me"),
          api.get("/titular/activation/status").catch(() => null),
        ]);
        const o = orgResp.data || {};
        setInitialData({
          name: o.name || "",
          address: o.address || "",
          city: o.city || "",
          phone: o.phone || "",
          description: o.description || "",
          working_days: o.working_days || [],
          working_hours_start: o.working_hours_start || "",
          working_hours_end: o.working_hours_end || "",
          slug: o.slug || "",
        });
        setActivationStatus(statusResp?.data || null);
      } catch (e) {
        toast.error("No se pudo cargar tu centro");
        setInitialData({});
      }
    })();
  }, [user?.id]);

  const steps = useMemo(() => ([
    {
      id: "center",
      label: "Mi centro",
      render: (props) => <StepCenter {...props} orgSlug={initialData?.slug} />,
    },
    {
      id: "services",
      label: "Mis servicios",
      render: (props) => <StepServices {...props} />,
    },
    {
      id: "availability",
      label: "Disponibilidad",
      render: (props) => <StepAvailability {...props} />,
    },
    {
      id: "value",
      label: "Primera acción de valor",
      render: (props) => <StepValue {...props} orgSlug={initialData?.slug} activationStatus={activationStatus} />,
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [initialData?.slug, activationStatus?.is_activated]);

  const handleComplete = async () => {
    try { await refresh?.(); } catch { /* noop */ }
    // Route to Dashboard where NBA card will guide next step (or celebrate activation)
    navigate("/");
  };

  const handleSkip = () => {
    // Skip = go straight to dashboard; NBA card will invite to resume
    navigate("/");
  };

  if (!initialData) {
    return (
      <div className="min-h-screen bg-[var(--ag-midnight)] p-6 flex items-center justify-center">
        <div className="w-full max-w-2xl space-y-4">
          <Skeleton className="h-24 rounded-sm" />
          <Skeleton className="h-40 rounded-sm" />
        </div>
      </div>
    );
  }

  return (
    <OnboardingWizard
      roleKey="titular"
      eyebrow="Configura AutoGlow · Titular"
      title="Bienvenido a tu ecosistema"
      subtitle="4 pasos rápidos y ya podrás recibir tu primera reserva."
      steps={steps}
      initialData={initialData}
      homeRoute="/"
      homeCta="Ir al panel"
      onComplete={handleComplete}
      onSkip={handleSkip}
      initialStepIdx={preferredIdx >= 0 ? preferredIdx : undefined}
    />
  );
}

/* ─────────── Step 1 · Mi centro ─────────── */
function StepCenter({ data, updateData }) {
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!data.name?.trim()) { toast.error("El nombre del centro es obligatorio"); return; }
    setSaving(true);
    try {
      await api.put("/org/me", {
        name: data.name.trim(),
        address: (data.address || "").trim() || null,
        city: (data.city || "").trim() || null,
        phone: (data.phone || "").trim() || null,
        description: (data.description || "").trim() || null,
      });
      toast.success("Centro guardado");
      updateData({ _savedAt: Date.now() });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="firstrun-step-center">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
        <Storefront weight="fill" /> Datos del centro
      </div>
      <h2 className="font-display text-2xl font-black tracking-tighter">Lo que verán tus clientes</h2>
      <p className="text-sm text-[var(--ag-text-dim)]">
        Solo lo esencial. Podrás afinar todo desde tus ajustes.
      </p>

      <div>
        <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Nombre del centro *</Label>
        <div className="relative mt-1">
          <Buildings size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
          <Input
            value={data.name || ""}
            onChange={(e) => updateData({ name: e.target.value })}
            placeholder="Ej. AutoGlow Barcelona"
            required
            className="pl-9"
            data-testid="firstrun-center-name"
          />
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Dirección</Label>
        <div className="relative mt-1">
          <MapPin size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
          <Input
            value={data.address || ""}
            onChange={(e) => updateData({ address: e.target.value })}
            placeholder="Calle, número, código postal"
            className="pl-9"
            data-testid="firstrun-center-address"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Ciudad</Label>
          <Input
            value={data.city || ""}
            onChange={(e) => updateData({ city: e.target.value })}
            placeholder="Barcelona"
            className="mt-1"
            data-testid="firstrun-center-city"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Teléfono</Label>
          <div className="relative mt-1">
            <PhoneIcon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
            <Input
              value={data.phone || ""}
              onChange={(e) => updateData({ phone: e.target.value })}
              placeholder="600 123 456"
              className="pl-9"
              data-testid="firstrun-center-phone"
            />
          </div>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Descripción breve (opcional)</Label>
        <Textarea
          value={data.description || ""}
          onChange={(e) => updateData({ description: e.target.value })}
          placeholder="Ej. Especialistas en detailing premium con más de 10 años."
          rows={3}
          className="mt-1"
          data-testid="firstrun-center-description"
        />
      </div>

      <div className="flex justify-end pt-1">
        <Button
          type="button"
          onClick={save}
          disabled={saving || !data.name?.trim()}
          className="ag-btn-gold"
          data-testid="firstrun-center-save"
        >
          {saving ? "Guardando…" : "Guardar y continuar"} <ArrowRight size={13} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

/* ─────────── Step 2 · Mis servicios ─────────── */
function StepServices({ data, updateData }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [duration, setDuration] = useState("60");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: r } = await api.get("/services");
      const items = Array.isArray(r) ? r : [];
      setServices(items);
      updateData({ services_count: items.length });
    } catch { setServices([]); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const addService = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || !price) return;
    setSaving(true);
    try {
      await api.post("/services", {
        name: name.trim(),
        price: Number(price),
        duration_min: Number(duration || 60),
      });
      toast.success("Servicio añadido");
      setName(""); setPrice(""); setDuration("60");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo añadir");
    } finally { setSaving(false); }
  };

  const suggestions = [
    { name: "Lavado exterior", price: 12, duration_min: 30 },
    { name: "Lavado completo", price: 25, duration_min: 60 },
    { name: "Detailing premium", price: 120, duration_min: 180 },
    { name: "Tapicería interior", price: 45, duration_min: 90 },
  ];

  return (
    <div className="space-y-4" data-testid="firstrun-step-services">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
        <Wrench weight="fill" /> Servicios
      </div>
      <h2 className="font-display text-2xl font-black tracking-tighter">¿Qué ofrece tu centro?</h2>
      <p className="text-sm text-[var(--ag-text-dim)]">
        Empieza por 1-3 servicios. Podrás editar, añadir y quitar más tarde.
      </p>

      <form onSubmit={addService} className="ag-card p-3 space-y-2" data-testid="firstrun-services-form">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_100px_90px] gap-2">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre · ej. Lavado completo"
            required
            data-testid="firstrun-services-name"
          />
          <div className="relative">
            <CurrencyEur size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
            <Input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="25"
              step="0.01"
              min="0"
              required
              className="pl-9"
              data-testid="firstrun-services-price"
            />
          </div>
          <Input
            type="number"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            placeholder="60"
            min="5"
            step="5"
            title="Duración en minutos"
            data-testid="firstrun-services-duration"
          />
        </div>
        <Button type="submit" disabled={saving || !name.trim() || !price} className="ag-btn-gold w-full h-9" data-testid="firstrun-services-add">
          <Plus size={13} className="mr-1" weight="bold" /> {saving ? "Añadiendo…" : "Añadir servicio"}
        </Button>
      </form>

      {/* Suggestions if empty */}
      {services.length === 0 && !loading && (
        <div className="text-[11px] text-[var(--ag-text-dim)]">
          <div className="mb-1.5 uppercase tracking-wider">Sugerencias rápidas</div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s.name}
                type="button"
                onClick={() => { setName(s.name); setPrice(String(s.price)); setDuration(String(s.duration_min)); }}
                className="px-2 py-1 border border-[var(--ag-border)] rounded text-[11px] hover:border-[var(--ag-gold)]/40 hover:text-[var(--ag-gold)]"
                data-testid={`firstrun-services-suggestion-${s.name.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {s.name} · {s.price}€
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Services list */}
      {loading ? (
        <Skeleton className="h-24 rounded-sm" />
      ) : services.length > 0 ? (
        <div className="ag-card p-0 divide-y divide-[var(--ag-border)]" data-testid="firstrun-services-list">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] bg-[var(--ag-midnight)]/40">
            {services.length} {services.length === 1 ? "servicio" : "servicios"}
          </div>
          {services.slice(0, 6).map((s) => (
            <div key={s.id} className="px-3 py-2 flex items-center justify-between text-sm">
              <span className="truncate">{s.name}</span>
              <span className="tabular-nums text-[var(--ag-gold)] font-bold">{Number(s.price).toFixed(2)}€</span>
            </div>
          ))}
          {services.length > 6 && (
            <div className="px-3 py-1.5 text-[11px] text-[var(--ag-text-dim)] text-center">
              +{services.length - 6} más
            </div>
          )}
        </div>
      ) : (
        <div className="p-3 border border-dashed border-[var(--ag-border)] rounded text-center text-xs text-[var(--ag-text-dim)]">
          Sin servicios todavía. Añade al menos uno para continuar.
        </div>
      )}
    </div>
  );
}

/* ─────────── Step 3 · Disponibilidad ─────────── */
function StepAvailability({ data, updateData }) {
  const [saving, setSaving] = useState(false);
  const days = data.working_days || [];
  const start = data.working_hours_start || "";
  const end = data.working_hours_end || "";

  const toggleDay = (d) => {
    const set = new Set(days);
    if (set.has(d)) set.delete(d); else set.add(d);
    updateData({ working_days: Array.from(set).sort() });
  };

  const save = async () => {
    if (days.length === 0) { toast.error("Selecciona al menos un día"); return; }
    if (!start || !end) { toast.error("Configura hora de inicio y cierre"); return; }
    setSaving(true);
    try {
      await api.put("/org/me", {
        working_days: days,
        working_hours_start: start,
        working_hours_end: end,
      });
      toast.success("Disponibilidad guardada");
      updateData({ _availabilitySaved: Date.now() });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo guardar");
    } finally { setSaving(false); }
  };

  const hasReal = days.length > 0 && start && end;

  return (
    <div className="space-y-4" data-testid="firstrun-step-availability">
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)]">
        <Clock weight="fill" /> Disponibilidad
      </div>
      <h2 className="font-display text-2xl font-black tracking-tighter">Cuándo puedes atender</h2>
      <p className="text-sm text-[var(--ag-text-dim)]">
        Sin datos ficticios. Configura los días y horas <span className="text-[var(--ag-text)] font-medium">reales</span> en los que abres.
      </p>

      <div>
        <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Días de la semana</Label>
        <div className="flex gap-1.5 mt-2 flex-wrap" data-testid="firstrun-availability-days">
          {DAYS_ES.map((d, i) => {
            const active = days.includes(i);
            return (
              <button
                key={i}
                type="button"
                onClick={() => toggleDay(i)}
                title={DAY_NAMES[i]}
                className={`min-w-[42px] h-10 rounded font-bold text-sm border transition-colors ${
                  active
                    ? "bg-[var(--ag-gold)] text-[var(--ag-midnight)] border-[var(--ag-gold)]"
                    : "bg-transparent text-[var(--ag-text-dim)] border-[var(--ag-border)] hover:text-white"
                }`}
                data-testid={`firstrun-availability-day-${i}`}
              >
                {d}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Apertura</Label>
          <Input
            type="time"
            value={start}
            onChange={(e) => updateData({ working_hours_start: e.target.value })}
            className="mt-1"
            data-testid="firstrun-availability-start"
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">Cierre</Label>
          <Input
            type="time"
            value={end}
            onChange={(e) => updateData({ working_hours_end: e.target.value })}
            className="mt-1"
            data-testid="firstrun-availability-end"
          />
        </div>
      </div>

      {!hasReal && (
        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-200">
          <strong>Horario no configurado</strong> · sin esto tus clientes no pueden reservar. AutoGlow no rellena
          valores por defecto para no engañar a nadie.
        </div>
      )}

      <div className="flex justify-end pt-1">
        <Button
          type="button"
          onClick={save}
          disabled={saving || !hasReal}
          className="ag-btn-gold"
          data-testid="firstrun-availability-save"
        >
          {saving ? "Guardando…" : "Guardar y continuar"} <ArrowRight size={13} className="ml-1.5" />
        </Button>
      </div>
    </div>
  );
}

/* ─────────── Step 4 · Primera acción de valor ─────────── */
function StepValue({ orgSlug, activationStatus }) {
  const isActivated = !!activationStatus?.is_activated;
  const publicUrl = orgSlug ? `/centro/${orgSlug}` : null;

  return (
    <div className="space-y-4 text-center" data-testid="firstrun-step-value">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-300 mx-auto">
        {isActivated ? <CheckCircle size={32} weight="fill" /> : <Sparkle size={32} weight="fill" />}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-gold)] font-semibold">
        Tu centro está preparado
      </div>
      <h2 className="font-display text-2xl font-black tracking-tighter">
        {isActivated ? "Empieza a captar clientes" : "Configura los pasos anteriores"}
      </h2>
      <p className="text-sm text-[var(--ag-text-dim)] max-w-md mx-auto">
        {isActivated
          ? "Comparte tu enlace público y consigue tus primeras reservas. Los clientes verán tus servicios, horarios y podrán reservar en segundos."
          : "Vuelve a los pasos anteriores para completar tu centro, servicios y horario. Desde aquí ya no puedes activarte."}
      </p>

      {publicUrl && (
        <div className="flex items-center justify-center gap-2 pt-2 flex-wrap">
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded font-medium text-sm ag-btn-gold"
            data-testid="firstrun-value-view-public"
          >
            <Storefront size={13} weight="fill" /> Ver mi centro público
          </a>
        </div>
      )}

      <div className="pt-2 text-[11px] text-[var(--ag-text-dim)]">
        Al pulsar <span className="text-[var(--ag-gold)]">Finalizar</span> irás a tu panel principal.
      </div>
    </div>
  );
}
